import { createContext, useContext, useEffect, useMemo, useRef } from "react";

/**
 * The FocusStackContext provides a way to manage multiple focus traps in a stack-like manner.
 * This is useful for scenarios where multiple modals or popups may be open at the same time,
 * ensuring that focus is correctly trapped within the topmost element.
 */
export const FocusStackContext = createContext<{
  insertFocusTrap: (trap: (e: KeyboardEvent) => void) => void;
  removeFocusTrap: (trap: (e: KeyboardEvent) => void) => void;
} | null>(null);

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], [role="button"]:not([disabled]), [role="option"]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a, [tabindex]:not([tabindex="-1"])';

/**
 * Refocuses the next focusable child element within the given parent element.
 */
export const refocusChildren = (
  parent: HTMLDivElement,
  direction: "forward" | "backward",
) => {
  if (!parent) {
    return false;
  }

  const focusables = parent.querySelectorAll(FOCUSABLE_SELECTOR);
  if (!focusables.length) {
    return false;
  }

  let firstFocusable: HTMLElement | null = null;
  let foundFocused = false;
  let didFocus = false;

  for (
    let i = direction === "forward" ? 0 : focusables.length - 1;
    direction === "forward" ? i < focusables.length : i >= 0;
    direction === "forward" ? i++ : i--
  ) {
    const el = focusables[i] as HTMLElement;
    const isFocusable =
      !el.hasAttribute("disabled") &&
      el.getAttribute("aria-hidden") !== "true" &&
      el.style.display !== "none";

    if (!firstFocusable && isFocusable) {
      firstFocusable = el;
    }

    if (el === document.activeElement) {
      foundFocused = true;
    } else if (foundFocused && isFocusable) {
      el.focus();
      didFocus = true;
      break;
    }
  }

  if (!didFocus && firstFocusable) {
    firstFocusable.focus();
    return true;
  }

  return didFocus;
};

/**
 * Creates a Tab-key focus trap for the given element.
 *
 * Focus moves to the container itself (expects tabIndex={-1}) so no control
 * looks focused on open; the first Tab moves to the first child (Shift+Tab
 * to the last) and focus stays trapped from there. A child that already has
 * focus (e.g. an `autoFocus` input) keeps it.
 */
export const createFocusTrap = (el: HTMLDivElement | null) => {
  if (el && !el.contains(document.activeElement)) {
    // preventScroll: a floating element may not be positioned yet when the
    // trap is created, and focusing it would scroll the page to its
    // initial (0, 0) position.
    el.focus({ preventScroll: true });
  }

  return (e: KeyboardEvent) => {
    if (!el) {
      return false;
    }
    if (e.key !== "Tab") {
      return false;
    }

    e.stopImmediatePropagation();
    e.preventDefault();

    const direction = e.shiftKey ? "backward" : "forward";
    const moved = refocusChildren(el, direction);
    if (!moved) {
      el.focus();
    }
  };
};

/**
 * Returns focus to `returnTo` once a focus trap on `container` is released.
 *
 * Deferred because React restores focus to the element that was active
 * before a commit once that commit finishes — for a container that stays
 * mounted while it animates out, that pulls focus straight back into it.
 * Focus is only moved if it is still inside the container (or was dropped
 * to the body), so a click on another control keeps its focus.
 */
export const restoreFocus = (
  returnTo: HTMLElement | null,
  container: HTMLElement | null,
) => {
  queueMicrotask(() => {
    if (!returnTo?.isConnected) {
      return;
    }
    const active = document.activeElement;
    const focusLost =
      !active || active === document.body || !!container?.contains(active);
    if (focusLost && active !== returnTo) {
      returnTo.focus({ preventScroll: true });
    }
  });
};

/**
 * Hook to manager focus traps in a stack-like manner.
 * If no stack has been provided via context, a new stack is created.
 * Once inside the stacked context components can register and unregister focus traps. The latest registered trap is the active one.
 * This ensures there is only ever one global focus handler active on the document, which makes it much easier to handle the logic of multiple modals/popups with focus traps.
 */
export const useFocusStack = () => {
  const focusContext = useContext(FocusStackContext);
  // Ref (not state) so the document listener always sees the latest stack
  // without tearing down/re-adding on every insert/remove.
  const focusStackRef = useRef<Array<(e: KeyboardEvent) => void>>([]);

  useEffect(() => {
    if (focusContext) {
      return;
    }

    const focusTrap = (e: KeyboardEvent) => {
      const stack = focusStackRef.current;
      if (stack.length === 0) {
        return;
      }
      stack[0]?.(e);
    };

    document.addEventListener("keydown", focusTrap, { capture: true });

    return () => {
      document.removeEventListener("keydown", focusTrap, { capture: true });
    };
  }, [focusContext]);

  const newFocusContext = useMemo(
    () => ({
      insertFocusTrap: (trap: (e: KeyboardEvent) => void) => {
        focusStackRef.current = [trap, ...focusStackRef.current];
      },
      removeFocusTrap: (trap: (e: KeyboardEvent) => void) => {
        focusStackRef.current = focusStackRef.current.filter((t) => t !== trap);
      },
    }),
    [],
  );

  if (focusContext) {
    return focusContext;
  }

  return newFocusContext;
};
